import { Box } from '@mui/material';
import { AdminDocumentLibraryTableRow } from 'components/document-library/AdminDocumentLibraryTableRow';
import PrimaryBtn from 'components/shared/Buttons/PrimaryButton';
import { useAdminDocumentLibrary } from 'hooks/dokumentLibrary/useAdminDocumentLibrary';
import usePdfDialog from 'hooks/usePdfDialog';
import React, { useEffect } from 'react';
import { useTranslation } from 'react-i18next';
import { ADMIN_DOCUMENT_LIBRARY_HEAD_CELLS } from 'utils/constans/tableHeads/adminDocumentLibraryTableHead';
import {
    AdminDocumentHeadCellContent,
    DocumentData,
    SortParams,
} from '../../../../models/DocumentLibrary';
import { PaginateOptions, TableSettings } from '../../../../models/SWRTable';
import { SWRTable } from '../../../shared/Table/SWRTable';
import { PageTitle } from '../../../shared/Typography/PageTitle';

export const AdminDocumentLibrary = () => {
    const { t } = useTranslation('documentLibrary');
    const [page, setPage] = React.useState<number>(1);
    const [rowsPerPage, setRowsPerPage] = React.useState<number>(25);
    const [search, setSearch] = React.useState<string>('');
    const [maxItems, setMaxItems] = React.useState<number>(0);
    const [column, setColumn] =
        React.useState<keyof AdminDocumentHeadCellContent>('last_modified');
    const [order, setOrder] = React.useState<'asc' | 'desc'>('desc');
    const [dialogOpen, setDialogOpen] = React.useState<boolean>(false);
    const { useAdminDocumentLibrarySWR } = useAdminDocumentLibrary();

    const pdfDialog = usePdfDialog();

    const { documents, isLoading, isError, forceRefresh } =
        useAdminDocumentLibrarySWR({
            page: page,
            size: rowsPerPage,
            search: search,
            column: column,
            order: order,
        });

    useEffect(() => {
        if (!isLoading && documents) {
            setMaxItems(documents.max_item);
        }
    }, [isLoading, documents]);

    const defaultPaginateOptions: PaginateOptions = {
        lazyPaginate: true,
        size: rowsPerPage,
        page: page - 1,
        search: search,
    };

    const tableSettings: TableSettings<AdminDocumentHeadCellContent> = {
        tableHeadSettings: {
            translateNs: 'documentLibrary',
            translatePrefix: 'TABLE_HEAD',
            selectable: false,
            headContent: ADMIN_DOCUMENT_LIBRARY_HEAD_CELLS,
        },
        tableToolbarSettings: {
            translateNs: 'documentLibrary',
            translatePrefix: 'TABLE_TOOLBAR',
        },
    };

    const handleChange = (size: number, page: number, filter: string): void => {
        switch (true) {
            case search != filter:
                setPage(1);
                setSearch(filter);
                break;
            case rowsPerPage != size:
                setPage(1);
                setRowsPerPage(size);
                break;
            default:
                setPage(page + 1);
        }
    };

    const handleShort = (
        props: SortParams<AdminDocumentHeadCellContent>
    ): void => {
        setColumn(props.property);
        setOrder(!props.isAsc ? 'asc' : 'desc');
    };

    const openUploadDialog = () => {
        pdfDialog.showPdfUploadDialog(true);
    };

    return (
        <>
            <Box
                sx={{
                    display: 'flex',
                    flexDirection: 'row',
                    justifyContent: 'space-between',
                }}
            >
                <PageTitle>{t('PAGE_TITLE')}</PageTitle>
                <PrimaryBtn
                    variant="contained"
                    component="label"
                    onClick={openUploadDialog}
                >
                    {t('UPLOAD_NEW_DOC')}
                </PrimaryBtn>
            </Box>
            {isError && <Box>{t('LOADING_ERROR')}</Box>}
            <SWRTable<DocumentData, AdminDocumentHeadCellContent>
                listItems={documents ? documents.documents : []}
                totalNumber={maxItems}
                component={AdminDocumentLibraryTableRow}
                handleChange={handleChange}
                paginateOptions={defaultPaginateOptions}
                tableSettings={tableSettings}
                isLoading={isLoading}
                handleShort={handleShort}
                forceRefresh={forceRefresh}
            />
        </>
    );
};
